import {
  Building2,
  FolderClosed,
  Library,
  ListChecks,
  type LucideIcon,
  Search,
  SquarePen,
} from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useBackofficeAccess } from "../../admin/access.ts";
import { useTenantLogo } from "../../brand/BrandMark.tsx";
import { EsiSymbol } from "../../brand/EsiMark.tsx";
import { NEW_CHAT_ID } from "../../conversation/actions.tsx";
import {
  areaOf,
  BACKOFFICE_PATH_ROOT,
  HOME_PATH,
  Link,
  PROJECTS_PATH,
  type Route,
} from "../../router.tsx";
import { AccountMenu } from "../AccountMenu.tsx";
import { NotificationBell } from "../NotificationBell.tsx";
import { TasksSlot } from "../TasksSlot.tsx";
import { ConversationTree } from "./ConversationTree.tsx";
import { RailTrigger } from "./RailTrigger.tsx";
import { SearchDialog, useSearchShortcut } from "./SearchDialog.tsx";

/**
 * The sidebar (spec `web-shell` "Sidebar navigation"): "New chat" and "Search" at the top, the
 * Projects and Recents tree, then Tasks and Knowledge, then Backoffice and the account menu at
 * the foot. Collapsed to icons it keeps "New chat", "Search", Projects, Tasks, Knowledge and
 * Backoffice, each named by its label and tooltip; on a phone it is a sheet that closes when a
 * link is followed.
 */
export function AppSidebar({ route, mark }: { route: Route; mark: ReactNode }) {
  const { t } = useTranslation();
  const backoffice = useBackofficeAccess();
  const { isMobile, setOpenMobile } = useSidebar();
  const area = areaOf(route);
  // Collapsed, the expand control shows the ESI symbol at rest, never in a tenant logo's place;
  // expanded, the logo is followed by the small ESI mark, named "on ESI".
  const tenantLogo = useTenantLogo();
  const [searching, setSearching] = useState(false);
  const openSearch = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    setSearching(true);
  }, [isMobile, setOpenMobile]);
  useSearchShortcut(openSearch);
  // On a phone, following a link closes the sheet.
  const closeOnMobile = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <Sidebar collapsible="icon" aria-label={t("nav.sidebar")}>
      <SidebarHeader className="h-12 flex-row py-0 items-center justify-between gap-1 px-3 group-data-[collapsible=icon]:h-auto group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2.5">
        <span className="flex min-w-0 items-center gap-1.5 group-data-[collapsible=icon]:hidden">
          {mark}
          {tenantLogo ? (
            // Co-branding (#285): the tenant's logo leads, the small ESI mark follows it in the same
            // row, named "on ESI" (its tooltip too); it hides with the logo on the collapsed rail.
            <Tooltip>
              <TooltipTrigger asChild>
                <span role="img" aria-label={t("nav.onEsi")} data-slot="esi-co-mark">
                  <EsiSymbol size={14} decorative className="block" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t("nav.onEsi")}</TooltipContent>
            </Tooltip>
          ) : null}
        </span>
        {isMobile ? null : (
          // Collapsed, the rail stacks the bell under its toggle (user decision 2026-10-30).
          <span className="flex items-center gap-1 group-data-[collapsible=icon]:flex-col">
            <NotificationBell />
            <RailTrigger mark={!tenantLogo} />
          </span>
        )}
      </SidebarHeader>
      <SidebarContent className="gap-0">
        <SidebarGroup className="pb-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <NavItem
                id={NEW_CHAT_ID}
                href={HOME_PATH}
                icon={SquarePen}
                label={t("nav.newChat")}
                current={area === "home"}
                onNavigate={closeOnMobile}
              />
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip={t("nav.search")}
                  aria-haspopup="dialog"
                  aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}
                  onClick={openSearch}
                >
                  <Search aria-hidden="true" strokeWidth={1.75} />
                  <span>{t("nav.search")}</span>
                  {isMobile ? null : (
                    <span
                      aria-hidden="true"
                      className="ml-auto text-[11px] text-fg-muted group-data-[collapsible=icon]:hidden"
                    >
                      <kbd>{mac ? "⌘K" : "Ctrl K"}</kbd>
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
              {/* Collapsed, the tree is hidden: Projects stays reachable as an icon. */}
              <NavItem
                href={PROJECTS_PATH}
                icon={FolderClosed}
                label={t("nav.projects")}
                current={route.name === "projects"}
                onNavigate={closeOnMobile}
                className="hidden group-data-[collapsible=icon]:flex"
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <ConversationTree route={route} onNavigate={closeOnMobile} />
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <nav aria-label={t("nav.more")}>
              <SidebarMenu>
                <NavItem
                  href="/tasks"
                  icon={ListChecks}
                  label={t("nav.tasks")}
                  current={area === "tasks"}
                  onNavigate={closeOnMobile}
                />
                <NavItem
                  href="/knowledge"
                  icon={Library}
                  label={t("nav.knowledge")}
                  current={area === "knowledge"}
                  onNavigate={closeOnMobile}
                />
              </SidebarMenu>
            </nav>
          </SidebarGroupContent>
        </SidebarGroup>
        <TasksSlot />
      </SidebarContent>
      <SidebarFooter className="gap-1">
        {backoffice === "yes" ? (
          <SidebarMenu>
            <NavItem
              href={BACKOFFICE_PATH_ROOT}
              icon={Building2}
              label={t("nav.backoffice")}
              current={area === "backoffice"}
              onNavigate={closeOnMobile}
            />
          </SidebarMenu>
        ) : null}
        <AccountMenu />
      </SidebarFooter>
      <SearchDialog open={searching} onClose={() => setSearching(false)} />
    </Sidebar>
  );
}

function NavItem(props: {
  id?: string;
  href: string;
  icon: LucideIcon;
  label: string;
  current: boolean;
  onNavigate: () => void;
  className?: string;
}) {
  const Icon = props.icon;
  return (
    <SidebarMenuItem className={props.className}>
      <SidebarMenuButton asChild isActive={props.current} tooltip={props.label}>
        <Link
          id={props.id}
          href={props.href}
          aria-current={props.current ? "page" : undefined}
          onClick={props.onNavigate}
        >
          <Icon aria-hidden="true" strokeWidth={1.75} />
          <span>{props.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
